import React, { useEffect, useState } from 'react'
import {
  DS_QUOTAS,
  DS_QUOTAS_PRICES,
  WM_QUOTAS,
  WM_QUOTAS_PRICES,
} from '@disco/state-management/actions/billing-provider-components'
import type { BillingProviderComponentsState } from '@disco/state-management/reducers/billing-provider-components'
import type { PricingFormState } from '@disco/state-management/reducers/forms/pricing'
import { UNLIMITED_WATERMARKING_QUOTA } from '@disco/utils/getWatermarkingQuota'
import { toCents } from '@disco/utils/pricing'
import DiscoverySuiteDescription from 'components/common/subscribed-user-billing/add-ons/discovery-suite-description'
const DISCOVERY_SUITE = 'discoverySuite'
const WATERMARKING = 'watermarking'
const baseDSContent = {
  id: DISCOVERY_SUITE,
  options: [],
  title: 'Discovery Suite',
  productName: 'Tracks',
  tooltip: `Your selected quota is applied separately for Auto tagging and Catalogs.
  If you select 1000 tracks, you get 1000 tracks auto tagged and 1000 tracks in your Catalogs.`,
  description: <DiscoverySuiteDescription />,
}
const baseWMContent = {
  id: WATERMARKING,
  options: [],
  title: 'Watermarking',
  productName: 'Playlists',
  description: (
    <>
      Securely watermark your tracks with DISCO’s fast, secure watermarking.{' '}
      <br />
      The 1 watermarked playlist plan allows you to have one playlist
      watermarked at a time. If you’ll be watermarking multiple releases at any
      one time, choose the 3 playlist or unlimited plan. Watermarking is an add
      on addition to your base plan.
    </>
  ),
}

const useContent = ({
  billingProviderComponents,
  pricingForm,
}: {
  pricingForm: PricingFormState
  billingProviderComponents: BillingProviderComponentsState
}): Array<
  | {
      description: React.ReactNode
      id: string
      options: Array<any>
      productName: string
      title: string
    }
  | {
      description: React.ReactNode
      id: string
      options: Array<any>
      productName: string
      title: string
      tooltip: string
    }
> => {
  const [DSContent, setDSContent] = useState(baseDSContent)
  const [WMContent, setWMContent] = useState(baseWMContent)
  useEffect(() => {
    if (pricingForm.plan && !billingProviderComponents.error) {
      const DSPrices = DS_QUOTAS_PRICES
      const WMPrices = WM_QUOTAS_PRICES
      const factorPeriod = pricingForm.paymentPeriod === 'annual' ? 1 / 0.9 : 1

      if (DSPrices) {
        const DSOptions = DS_QUOTAS.map(quota => {
          const option = DSPrices[String(quota)]

          if (option) {
            const price = option.amount_in_cents / factorPeriod / 100
            return {
              value: quota * 1000,
              label: `${quota * 1000} Tracks - $${toCents(price)} per month`,
              price: price,
            }
          }

          return null
        })
        setDSContent({ ...DSContent, options: DSOptions })
      }
      if (WMPrices) {
        const WMOptions = WM_QUOTAS.map(quota => {
          const option = WMPrices[String(quota)]

          if (option) {
            const price = option.amount_in_cents / factorPeriod / 100
            return {
              value: quota,
              label: `${
                UNLIMITED_WATERMARKING_QUOTA === quota ? 'Unlimited' : quota
              } watermarked ${
                quota > 1 ? 'playlists' : 'playlist'
              } - $${toCents(price)} per month`,
              price: price,
            }
          }

          return null
        })
        setWMContent({ ...WMContent, options: WMOptions })
      }
    }
  }, [pricingForm, billingProviderComponents])

  return [DSContent, WMContent]
}

export default useContent
